import dayjs from 'dayjs/esm/index';
import 'dayjs/esm/locale/ru';

import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfterPlugin from 'dayjs/esm/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isLeapYear from 'dayjs/plugin/isLeapYear';

import { DATE_PATTERN } from '../constants/REGEXP_PATTERNS';
import { ERROR_DATE } from '../constants/errors';

dayjs.locale('ru');
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfterPlugin);
dayjs.extend(isSameOrBefore);
dayjs.extend(isLeapYear);
export const DATE_FORMAT = {
  VISIBLE: 'DD.MM.YYYY',
  TO_SEND: 'YYYY-MM-DD',
  FOR_TEXT: 'D MMMM YYYY',
  DRAFT_SAVED_TIME: 'D MMMM YYYY в HH:mm',
};
export const dateNow = dayjs(dayjs(), DATE_FORMAT.VISIBLE);

const dayjsMinAcceptableDate = dateNow.subtract(150, 'y');
export const passportMinIssueDate = dayjs('01.10.1997', DATE_FORMAT.VISIBLE);
export const firstPassportExpirationDate = 20;
export const secondPassportExpirationDate = 45;
export const givenDaysForPassportUpdate = 90;

export const minAcceptableDate = dayjsMinAcceptableDate.format(
  DATE_FORMAT.VISIBLE
);
export const maxAcceptableDate = dateNow.format(DATE_FORMAT.VISIBLE);

export function shouldHaveIdentification(date: string): boolean {
  return dayjs()
    .startOf('d')
    .isSameOrAfter(
      dayjs(date, DATE_FORMAT.VISIBLE)
        .add(14, 'year')
        .add(91, 'day')
        .startOf('d')
    );
}

export function shouldBeGettingEducation(date: string): boolean {
  return dayjs()
    .startOf('d')
    .isSameOrAfter(dayjs(date, DATE_FORMAT.VISIBLE).add(16, 'y').startOf('d'));
}

export function isUnder23YearsOld(date: string): boolean {
  return dayjs(date, DATE_FORMAT.VISIBLE)
    .add(23, 'y')
    .startOf('d')
    .isAfter(dayjs().startOf('d'));
}

export function isAdult(date: string): boolean {
  return dayjs()
    .startOf('d')
    .isSameOrAfter(dayjs(date, DATE_FORMAT.VISIBLE).add(18, 'y').startOf('d'));
}

export const isDateInvalid = (value: string = ''): boolean => {
  if (!value.length) return true;
  const matchPattern = new RegExp(DATE_PATTERN, 'gi').test(value);

  const [day, month, year] = value.split('.');
  const shortMonths = [3, 5, 8, 10];
  const jsDay = parseInt(day, 10);
  if (jsDay < 1) return true;

  const jsMonth = parseInt(month, 10) - 1;

  if (jsMonth > 11 || jsMonth < 0) return true;
  const isFebruary = jsMonth === 1;
  const isShort = !isFebruary && shortMonths.includes(jsMonth);

  if (isFebruary && jsDay > (dayjs(`${year}-01-01`).isLeapYear() ? 29 : 28))
    return true;
  if (jsDay > (isShort ? 30 : 31)) return true;
  const date = dayjs(value, 'DD.MM.YYYY', 'ru');

  return !(
    matchPattern &&
    date.isValid() &&
    !date.isSameOrAfter(dateNow) &&
    !date.isSameOrBefore(dayjsMinAcceptableDate)
  );
};

export function formatDateToSubmit(date: string): string {
  return !isDateInvalid(date)
    ? dayjs(date, DATE_FORMAT.VISIBLE).format(DATE_FORMAT.TO_SEND)
    : '';
}

export const isDateBeforeThan: (earlier: string, later: string) => boolean = (
  earlier,
  later
) => {
  return dayjs(earlier, DATE_FORMAT.VISIBLE).isBefore(
    dayjs(later, DATE_FORMAT.VISIBLE)
  );
};

export const isDateBeforeOrSameThan: (
  earlier: string,
  later: string
) => boolean = (earlier, later) => {
  return dayjs(earlier, DATE_FORMAT.VISIBLE).isSameOrBefore(
    dayjs(later, DATE_FORMAT.VISIBLE)
  );
};

export const isDateInAcceptableRange = (date) => {
  const dayjsDate = dayjs(date, DATE_FORMAT.VISIBLE);

  const isValidDate = dayjsDate.isValid();

  const isInFuture =
    isValidDate && dayjsDate.isAfter(dateNow) ? ERROR_DATE.IS_IN_FUTURE : false;

  const isAwfullyOld =
    isValidDate && dayjsDate.isBefore(dayjsMinAcceptableDate)
      ? ERROR_DATE.IS_IN_PAST
      : false;

  return isInFuture || isAwfullyOld || isDateInvalid(date);
};

export const getDateAfterThis = (date: string): string => {
  const dayjsDate = dayjs(date, DATE_FORMAT.VISIBLE);
  const dayAfter = dayjsDate.add(1, 'day');

  return dayAfter.format(DATE_FORMAT.VISIBLE);
};

export const getDateBeforeThis = (date: string): string => {
  const dayjsDate = dayjs(date, DATE_FORMAT.VISIBLE);
  const dayBefore = dayjsDate.subtract(1, 'day');

  return dayBefore.format(DATE_FORMAT.VISIBLE);
};

export const getDraftSaveDate = (date: string): string => {
  return dayjs(date).format(DATE_FORMAT.DRAFT_SAVED_TIME);
};

export const sortDates: (earlier: string, later: string) => number = (
  earlier,
  later
) => {
  return dayjs(earlier).isSameOrAfter(dayjs(later)) ? -1 : 0;
};

export function formatDateFromDraft(date: string): string {
  return date
    ? dayjs(date, DATE_FORMAT.TO_SEND).format(DATE_FORMAT.VISIBLE)
    : '';
}
